import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { filterByEnabledSince } from '../../services/notificationFilter';

interface ReviewResult {
  id: string;
  trackId: string;
  trackName: string;
  decision: 'approved' | 'rejected';
  reviewedAt: number;
  reviewedByName: string;
  reviewedByEmail?: string;
  reviewedVia?: 'in-app' | 'external-link';
  rejectionReason?: string;
  clientOrgName?: string;
}

interface ReviewResultsModalProps {
  firebase: any;
}

const ReviewResultsModal: React.FC<ReviewResultsModalProps> = ({ firebase }) => {
  const [results, setResults] = useState<ReviewResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(new Set());
  const [acknowledgingAll, setAcknowledgingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  const isOpen = useNotificationsStore(state => state.showReviewResultsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowReviewResultsModal);
  const setReviewResultsCount = useNotificationsStore(state => state.setReviewResultsCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Real-time listener for review results
  useEffect(() => {
    if (!currentOrgId || !firebase?.subscribeReviewResultsForOrg) {
      setResults([]);
      setReviewResultsCount(0);
      return;
    }

    setLoading(true);
    const unsubscribe = firebase.subscribeReviewResultsForOrg(
      currentOrgId,
      (fetchedResults: ReviewResult[]) => {
        const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
        const filtered = filterByEnabledSince('reviewNotifications', fetchedResults, enabledSince, (n) => n.reviewedAt);
        setResults(filtered);
        setReviewResultsCount(filtered.length);
        setLoading(false);

        // Sync track approval_status from notifications
        // (needed when external reviewers approve — they can't update audiometa directly)
        fetchedResults.forEach(async (result) => {
          if (result.trackId && result.decision && firebase) {
            try {
              await firebase.updateTrackApprovalStatus(currentOrgId, result.trackId, result.decision);
            } catch {
              // Ignore — best effort sync
            }
          }
        });

        // Auto-open on first load if there are results
        if (!hasAutoOpened && fetchedResults.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, firebase]);

  // Close modal when all items are processed
  useEffect(() => {
    if (isOpen && results.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [results.length, isOpen, loading, setIsOpen]);

  const handleAcknowledge = async (result: ReviewResult) => {
    if (!currentOrgId || !firebase) return;

    setAcknowledgingIds(prev => new Set(prev).add(result.id));
    try {
      await firebase.dismissReviewResult(currentOrgId, result.id);
      // Real-time listener will update the list
    } catch (error) {
      console.error('Error acknowledging review result:', error);
    } finally {
      setAcknowledgingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(result.id);
        return newSet;
      });
    }
  };

  const handleAcknowledgeAll = async () => {
    if (!currentOrgId || !firebase) return;

    setAcknowledgingAll(true);
    try {
      await Promise.all(
        results.map(result => firebase.dismissReviewResult(currentOrgId, result.id))
      );
      // Real-time listener will update the list and close modal
    } catch (error) {
      console.error('Error acknowledging all review results:', error);
    } finally {
      setAcknowledgingAll(false);
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  const totalItems = results.length;

  if (!isOpen || totalItems === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleSkip}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          Review Updates ({totalItems})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            {/* Approved spots */}
            {results.filter(r => r.decision === 'approved').length > 0 && (
              <>
                <p style={{ marginBottom: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                  <strong style={{ color: '#50c878' }}>Approved</strong> - The following spots have been approved
                </p>
                <div style={{ marginBottom: results.some(r => r.decision === 'rejected') ? '1.5rem' : 0 }}>
                  {results.filter(r => r.decision === 'approved').map((result) => (
                    <div
                      key={result.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem 1rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'rgba(80, 200, 120, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(80, 200, 120, 0.3)',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {result.trackName}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                          By: {result.reviewedByName}
                          {result.reviewedVia === 'external-link' ? ' (via link)' : ' (in-app)'}
                          {' | '}
                          {moment(result.reviewedAt).format('DD/MM/YYYY HH:mm')}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="btn-primary-custom ml-3"
                        onClick={() => handleAcknowledge(result)}
                        disabled={acknowledgingIds.has(result.id) || acknowledgingAll}
                        style={{ minWidth: '100px' }}
                      >
                        {acknowledgingIds.has(result.id) ? (
                          <Spinner size="sm" />
                        ) : (
                          'Acknowledge'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Rejected spots */}
            {results.filter(r => r.decision === 'rejected').length > 0 && (
              <>
                <p style={{ marginBottom: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                  <strong style={{ color: '#ff5050' }}>Rejected</strong> - The following spots were rejected
                </p>
                <div>
                  {results.filter(r => r.decision === 'rejected').map((result) => (
                    <div
                      key={result.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '0.75rem 1rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'rgba(255, 80, 80, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 80, 80, 0.3)',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {result.trackName}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                          By: {result.reviewedByName}
                          {result.reviewedVia === 'external-link' ? ' (via link)' : ' (in-app)'}
                          {' | '}
                          {moment(result.reviewedAt).format('DD/MM/YYYY HH:mm')}
                        </div>
                        {result.rejectionReason && (
                          <div style={{
                            fontSize: '0.8rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginTop: '0.5rem',
                            fontStyle: 'italic',
                          }}>
                            Reason: {result.rejectionReason}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="btn-primary-custom ml-3"
                        onClick={() => handleAcknowledge(result)}
                        disabled={acknowledgingIds.has(result.id) || acknowledgingAll}
                        style={{ minWidth: '100px' }}
                      >
                        {acknowledgingIds.has(result.id) ? (
                          <Spinner size="sm" />
                        ) : (
                          'Acknowledge'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          className="btn-secondary-custom"
          onClick={handleSkip}
          disabled={acknowledgingAll}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleAcknowledgeAll}
          disabled={acknowledgingAll || results.length === 0}
        >
          {acknowledgingAll ? (
            <>
              <Spinner size="sm" className="mr-2" /> Acknowledging...
            </>
          ) : (
            <span style={{ position: 'relative', zIndex: 2 }}>
              Acknowledge All ({totalItems})
            </span>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReviewResultsModal;
